import React, { useEffect, useRef, useState } from 'react';
import { X, Upload, Search, Loader2, FileText, AlertCircle, AlertTriangle, Check } from 'lucide-react';
import {
  useContracts,
  useUploadContractFile,
  useExtractRepositoryContract,
  useCreateRepositoryContract,
  useSuppliers,
  useActiveContractGroups
} from '../../../hooks/queries';
import type { RepositoryExtraction } from '../../../services/api/contractService';
import {
  RepositoryContractForm,
  RepositoryContractFormValues,
  EMPTY_FORM,
  validateForm,
  toNewRepositoryContract
} from './RepositoryContractForm';
import { formFromExtraction } from './batchImport';
import { ACCEPTED_EXTENSIONS, checkContractFile } from './fileChecks';

interface AddToRepositoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a contract or document is saved, so other views can refresh. */
  onAdded?: () => void;
}

type Mode = 'new' | 'existing';
type NewStage = 'choose' | 'reading' | 'review';

const inputClass =
  'w-full px-3 py-2 text-sm bg-white dark:bg-dark-bg-secondary border border-gray-300 dark:border-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-accent-400 focus:border-primary-500 dark:focus:border-accent-400 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors';

const primaryButton =
  'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-br from-primary-600 to-primary-500 dark:from-accent-500 dark:to-accent-400 dark:text-gray-900 rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed';
const secondaryButton =
  'px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors disabled:opacity-50';

/**
 * Add a signed contract to the repository.
 *
 * New contract: the document is read by AI, the details are filled in for a
 * person to check and correct, and saving creates the contract with its
 * signed document. Existing contract: the signed document is attached to a
 * contract that's already in the app.
 */
export const AddToRepositoryModal: React.FC<AddToRepositoryModalProps> = ({ isOpen, onClose, onAdded }) => {
  const [mode, setMode] = useState<Mode>('new');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // New contract
  const [stage, setStage] = useState<NewStage>('choose');
  const [extraction, setExtraction] = useState<RepositoryExtraction | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [form, setForm] = useState<RepositoryContractFormValues>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<ReturnType<typeof validateForm>>({});
  // Identifies the latest read, so a slow result for an abandoned file is ignored.
  const readIdRef = useRef(0);

  // Existing contract
  const [contractSearchInput, setContractSearchInput] = useState('');
  const [contractSearch, setContractSearch] = useState('');
  const [contractId, setContractId] = useState('');
  const [versionLabel, setVersionLabel] = useState('');
  const [notes, setNotes] = useState('');

  const extract = useExtractRepositoryContract();
  const create = useCreateRepositoryContract();
  const upload = useUploadContractFile();
  const { data: suppliers = [] } = useSuppliers({ enabled: isOpen });
  const { data: groups = [] } = useActiveContractGroups({ enabled: isOpen });

  useEffect(() => {
    const timer = setTimeout(() => setContractSearch(contractSearchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [contractSearchInput]);

  // getAll serialises every key it's given, so leave search out rather than undefined.
  const { data: contracts = [], isFetching: isSearching } = useContracts(
    { ...(contractSearch ? { search: contractSearch } : {}), limit: 20, sortBy: 'updatedAt', sortOrder: 'desc' },
    { enabled: isOpen && mode === 'existing' }
  );

  // Start clean every time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    readIdRef.current += 1;
    setMode('new');
    setFile(null);
    setFileError(null);
    setStage('choose');
    setExtraction(null);
    setReadError(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setContractSearchInput('');
    setContractSearch('');
    setContractId('');
    setVersionLabel('');
    setNotes('');
    create.reset();
    upload.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const isSaving = create.isPending || upload.isPending;

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, isSaving]);

  if (!isOpen) return null;

  const readDocument = (picked: File) => {
    const readId = ++readIdRef.current;
    setStage('reading');
    setExtraction(null);
    setReadError(null);
    extract.mutate(picked, {
      onSuccess: (result) => {
        if (readIdRef.current !== readId) return;
        setExtraction(result);
        setForm(formFromExtraction(result));
        setFormErrors({});
        setStage('review');
      },
      onError: (err) => {
        if (readIdRef.current !== readId) return;
        setReadError(err instanceof Error ? err.message : 'The document could not be read.');
        setForm(EMPTY_FORM);
        setStage('review');
      }
    });
  };

  const fillInByHand = () => {
    readIdRef.current += 1;
    setExtraction(null);
    setReadError(null);
    setForm(EMPTY_FORM);
    setStage('review');
  };

  const pickFile = (picked: File | undefined) => {
    if (!picked) return;
    const problem = checkContractFile(picked);
    setFileError(problem);
    if (problem) return;
    setFile(picked);
    create.reset();
    upload.reset();
    if (mode === 'new') readDocument(picked);
  };

  const switchMode = (next: Mode) => {
    if (next === mode) return;
    setMode(next);
    create.reset();
    upload.reset();
    // A file chosen in "existing" mode hasn't been read yet.
    if (next === 'new' && file && stage === 'choose') readDocument(file);
  };

  const addAsNewVersionOfDuplicate = () => {
    if (!extraction?.duplicate) return;
    readIdRef.current += 1;
    setContractId(extraction.duplicate._id);
    setContractSearchInput(extraction.duplicate.contractNumber || extraction.duplicate.contractTitle);
    setVersionLabel(form.versionLabel);
    setNotes(form.notes);
    setMode('existing');
  };

  const saveNew = () => {
    if (!file) return;
    const errors = validateForm(form);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    create.mutate(
      {
        file,
        details: toNewRepositoryContract(form)
      },
      {
        onSuccess: () => {
          onAdded?.();
          onClose();
        }
      }
    );
  };

  const saveExisting = () => {
    if (!contractId || !file) return;
    // 'signed' is what puts a contract in the repository.
    const metadata: Record<string, string> = { versionType: 'signed' };
    if (versionLabel.trim()) metadata.versionLabel = versionLabel.trim();
    if (notes.trim()) metadata.notes = notes.trim();
    upload.mutate(
      { contractId, file, metadata },
      {
        onSuccess: () => {
          onAdded?.();
          onClose();
        }
      }
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === 'new') saveNew();
    else saveExisting();
  };

  const contractKey = (c: { _id?: string; id?: string | number }) => String(c._id ?? c.id ?? '');

  const dropzone = (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragging(false);
          pickFile(e.dataTransfer.files?.[0]);
        }}
        className={`flex flex-col items-center gap-2 px-4 text-center rounded-lg border-2 border-dashed transition-colors ${
          file ? 'py-4' : 'py-8'
        } ${isDragging ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-300 dark:border-dark-border'}`}
      >
        {file ? (
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-6 h-6 flex-shrink-0 text-primary-500 dark:text-accent-400" aria-hidden />
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{file.name}</p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isSaving}
              className="flex-shrink-0 text-xs font-medium text-primary-700 dark:text-accent-400 hover:underline"
            >
              Change
            </button>
          </div>
        ) : (
          <>
            <Upload className="w-8 h-8 text-gray-400" aria-hidden />
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Drop the signed contract here or{' '}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="font-medium text-primary-700 dark:text-accent-400 hover:underline"
              >
                browse
              </button>
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {mode === 'new' ? 'PDF or Word is read automatically. ' : ''}Up to 50 MB.
            </p>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={(e) => {
            pickFile(e.target.files?.[0]);
            e.target.value = '';
          }}
          className="sr-only"
          aria-label="Choose the signed contract"
        />
      </div>
      {fileError && <p className="mt-1 text-xs text-rose-600 dark:text-rose-400">{fileError}</p>}
    </div>
  );

  const saveError = (mode === 'new' ? create.error : upload.error) as Error | null;
  const canSubmit =
    mode === 'new' ? Boolean(file) && stage === 'review' && !isSaving : Boolean(file && contractId) && !isSaving;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !isSaving && onClose()} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-to-repository-title"
        className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-white dark:bg-dark-surface rounded-2xl shadow-xl border border-gray-100 dark:border-dark-border"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-dark-border">
          <h2 id="add-to-repository-title" className="text-lg font-semibold text-gray-900 dark:text-white">
            Add signed contract
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Close"
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-surface-raised transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="px-6 py-5 space-y-5 overflow-y-auto">
            {/* Mode */}
            <div role="tablist" aria-label="Where this contract goes" className="grid grid-cols-2 p-1 bg-gray-100 dark:bg-dark-bg-secondary rounded-lg">
              {(
                [
                  { key: 'new', label: 'New contract' },
                  { key: 'existing', label: 'Existing contract' }
                ] as { key: Mode; label: string }[]
              ).map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={mode === tab.key}
                  onClick={() => switchMode(tab.key)}
                  disabled={isSaving}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    mode === tab.key
                      ? 'bg-white dark:bg-dark-surface text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {dropzone}

            {mode === 'new' && stage === 'reading' && (
              <div role="status" className="flex flex-col items-center gap-2 py-8 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary-500 dark:text-accent-400" aria-hidden />
                <p className="text-sm font-medium text-gray-900 dark:text-white">Reading the contract…</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">This usually takes under a minute.</p>
                <button type="button" onClick={fillInByHand} className="mt-1 text-xs font-medium text-primary-700 dark:text-accent-400 hover:underline">
                  Fill in by hand instead
                </button>
              </div>
            )}

            {mode === 'new' && stage === 'review' && (
              <>
                {(readError || extraction?.extractionError) && (
                  <div role="alert" className="flex gap-2 p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden />
                    {extraction?.extractionError || `The document couldn't be read (${readError}). Fill in the details by hand.`}
                  </div>
                )}
                {extraction?.duplicate && (
                  <div role="alert" className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800">
                    <span className="flex gap-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden />
                      <span>
                        <strong>{extraction.duplicate.contractTitle}</strong> already has contract number{' '}
                        {extraction.duplicate.contractNumber}.
                      </span>
                    </span>
                    <button type="button" onClick={addAsNewVersionOfDuplicate} className="flex-shrink-0 font-medium underline">
                      Add as a new version of it
                    </button>
                  </div>
                )}
                <RepositoryContractForm
                  values={form}
                  onChange={(values) => {
                    setForm(values);
                    if (Object.keys(formErrors).length > 0) setFormErrors(validateForm(values));
                  }}
                  errors={formErrors}
                  suppliers={suppliers}
                  groups={groups}
                  extracted={extraction?.extracted ?? null}
                  supplierMatched={Boolean(extraction?.supplierMatch)}
                />
              </>
            )}

            {mode === 'existing' && (
              <>
                <div>
                  <label htmlFor="repo-contract-search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contract
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input
                      id="repo-contract-search"
                      type="search"
                      value={contractSearchInput}
                      onChange={(e) => setContractSearchInput(e.target.value)}
                      placeholder="Search by name or number…"
                      className={`${inputClass} pl-9`}
                    />
                    {isSearching && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-gray-400" />
                    )}
                  </div>
                  <ul
                    role="listbox"
                    aria-label="Contracts"
                    className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-gray-200 dark:border-dark-border divide-y divide-gray-100 dark:divide-dark-border"
                  >
                    {contracts.length === 0 ? (
                      <li className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {isSearching ? 'Searching…' : 'No contracts found.'}
                      </li>
                    ) : (
                      contracts.map((c) => {
                        const id = contractKey(c);
                        const selected = id === contractId;
                        return (
                          <li key={id} role="option" aria-selected={selected}>
                            <button
                              type="button"
                              onClick={() => setContractId(id)}
                              className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left transition-colors ${
                                selected ? 'bg-primary-50 dark:bg-primary-900/30' : 'hover:bg-gray-50 dark:hover:bg-dark-bg-secondary'
                              }`}
                            >
                              <span className="min-w-0">
                                <span className="block text-sm text-gray-900 dark:text-white truncate">{c.contractTitle}</span>
                                <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {[c.contractNumber, c.supplier?.companyName || c.supplier?.name, c.year].filter(Boolean).join(' · ')}
                                </span>
                              </span>
                              {selected && <Check className="w-4 h-4 flex-shrink-0 text-primary-600 dark:text-accent-400" />}
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                </div>
                <div>
                  <label htmlFor="repo-existing-label" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Document label <span className="font-normal text-gray-500">(optional)</span>
                  </label>
                  <input
                    id="repo-existing-label"
                    type="text"
                    value={versionLabel}
                    onChange={(e) => setVersionLabel(e.target.value)}
                    placeholder="e.g. Executed copy, 2026 renewal"
                    maxLength={100}
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="repo-existing-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes <span className="font-normal text-gray-500">(optional)</span>
                  </label>
                  <textarea
                    id="repo-existing-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    maxLength={1000}
                    className={inputClass}
                  />
                </div>
              </>
            )}

            {saveError && (
              <div role="alert" className="flex items-center gap-2 p-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg dark:bg-rose-900/20 dark:text-rose-300 dark:border-rose-800">
                <AlertCircle className="w-4 h-4 flex-shrink-0" aria-hidden />
                {saveError.message || 'Saving failed. Try again.'}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 dark:border-dark-border">
            <button type="button" onClick={onClose} disabled={isSaving} className={secondaryButton}>
              Cancel
            </button>
            <button type="submit" disabled={!canSubmit} className={primaryButton}>
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {isSaving ? 'Saving…' : mode === 'new' ? 'Add to repository' : 'Attach signed document'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddToRepositoryModal;
